import { useState, useEffect } from "react";

// ── CONSTANTS ──────────────────────────────────────────────────────────────
const SERVICES = [
  { id: "physio",  name: "Physiotherapy",       dur: "60 min" },
  { id: "sports",  name: "Sports Rehab",         dur: "60 min" },
  { id: "manual",  name: "Manual Therapy",       dur: "60 min" },
  { id: "dry",     name: "Dry Needling",         dur: "45 min" },
  { id: "posture", name: "Posture Assessment",   dur: "60 min" },
  { id: "hydro",   name: "Hydrotherapy",         dur: "60 min" },
];

const SCHEDULE = [
  { time: "9:30–10:30",  type: "slot"   },
  { time: "10:30–10:45", type: "gap"    },
  { time: "10:45–11:45", type: "slot"   },
  { time: "11:45–12:00", type: "gap"    },
  { time: "12:00–1:00",  type: "slot"   },
  { time: "1:00–1:15",   type: "gap"    },
  { time: "1:15–2:00",   type: "lunch"  },
  { time: "2:00–3:00",   type: "slot"   },
  { time: "3:00–3:15",   type: "gap"    },
  { time: "3:15–4:15",   type: "slot"   },
  { time: "4:15–4:30",   type: "gap"    },
  { time: "4:30–5:30",   type: "slot"   },
  { time: "5:30–5:45",   type: "gap"    },
  { time: "5:45–6:45",   type: "slot"   },
  { time: "6:45–7:00",   type: "gap"    },
  { time: "7:00–8:00",   type: "slot"   },
];

// All cabins in priority order for auto-assignment
// Cabin 4 & 5 only cover slots up to index 12
const CABINS = [
  { name: "Cabin 1", maxSlotIdx: 15 },
  { name: "Cabin 2", maxSlotIdx: 15 },
  { name: "Cabin 3", maxSlotIdx: 15 },
  { name: "Cabin 4", maxSlotIdx: 12 },
  { name: "Cabin 5", maxSlotIdx: 12 },
  { name: "Flexible/VIP 1", maxSlotIdx: 15 },
  { name: "Flexible/VIP 2", maxSlotIdx: 15 },
];

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// ── AUTO-ASSIGN CABIN ─────────────────────────────────────────────────────
// Pass in already-booked { date, time } pairs fetched from your API.
// Returns the first cabin that supports the slot index AND isn't already taken.
function autoAssignCabin(date, slotTime, existingBookings = []) {
  const slotIdx = SCHEDULE.findIndex(s => s.time === slotTime);
  for (const cabin of CABINS) {
    if (slotIdx > cabin.maxSlotIdx) continue; // cabin doesn't cover this time
    const alreadyTaken = existingBookings.some(
      b => b.date === date && b.time === slotTime && b.cabin === cabin.name
    );
    if (!alreadyTaken) return cabin.name;
  }
  return null; // all cabins full for this slot
}

// ── STYLES ─────────────────────────────────────────────────────────────────
const C = {
  teal:       "#1D9E75",
  tealLight:  "#E1F5EE",
  tealMid:    "#5DCAA5",
  tealDark:   "#0F6E56",
  amber:      "#EF9F27",
  amberLight: "#FAEEDA",
  grayBg:     "#F7F6F2",
  grayBorder: "#E2E0D8",
  textDark:   "#2C2C2A",
  textMid:    "#5F5E5A",
  textLight:  "#888780",
  white:      "#ffffff",
  red:        "#E24B4A",
  redLight:   "#FCEBEB",
};

const s = {
  page:       { fontFamily: "'DM Sans', sans-serif", background: C.grayBg, minHeight: "100vh", color: C.textDark },
  hero:       { background: C.tealDark, padding: "2.5rem 2rem 2rem", textAlign: "center", position: "relative", overflow: "hidden" },
  heroLogo:   { fontFamily: "'Playfair Display', serif", fontSize: "1.7rem", color: "#fff", letterSpacing: "0.5px", marginBottom: "0.3rem" },
  heroSub:    { fontSize: "0.75rem", color: "rgba(255,255,255,0.55)", letterSpacing: "2.5px", textTransform: "uppercase", marginBottom: "1.25rem" },
  heroTitle:  { fontFamily: "'Playfair Display', serif", fontSize: "2rem", color: "#fff", fontWeight: 400, lineHeight: 1.3, marginBottom: "0.4rem" },
  heroDesc:   { fontSize: "0.88rem", color: "rgba(255,255,255,0.6)", maxWidth: 420, margin: "0 auto" },
  stepsBar:   { background: C.white, borderBottom: `1px solid ${C.grayBorder}`, display: "flex", justifyContent: "center" },
  main:       { maxWidth: 860, margin: "2rem auto", padding: "0 1rem 4rem" },
  card:       { background: C.white, borderRadius: 16, border: `1px solid ${C.grayBorder}`, padding: "1.75rem", marginBottom: "1.25rem" },
  cardTitle:  { fontFamily: "'Playfair Display', serif", fontSize: "1.15rem", fontWeight: 600, color: C.textDark, marginBottom: "0.2rem" },
  cardHint:   { fontSize: "0.78rem", color: C.textLight, marginBottom: "1.25rem" },
  label:      { fontSize: "0.73rem", fontWeight: 500, color: C.textMid, textTransform: "uppercase", letterSpacing: "0.8px", display: "block", marginBottom: 5 },
  input:      { fontFamily: "'DM Sans', sans-serif", fontSize: "0.9rem", color: C.textDark, background: C.grayBg, border: `1.5px solid ${C.grayBorder}`, borderRadius: 8, padding: "0.6rem 0.85rem", outline: "none", width: "100%", transition: "border-color 0.2s" },
  btnPrimary: { width: "100%", padding: "0.85rem", background: C.teal, color: "#fff", border: "none", borderRadius: 10, fontFamily: "'DM Sans', sans-serif", fontSize: "0.95rem", fontWeight: 500, cursor: "pointer", marginTop: "1rem", transition: "background 0.2s" },
  summaryCard:{ background: C.tealDark, borderRadius: 16, padding: "1.5rem", color: "#fff", position: "sticky", top: "1rem" },
};

// ── HELPERS ─────────────────────────────────────────────────────────────────
function getNext14Days() {
  const days = [];
  const today = new Date();
  for (let i = 0; i < 21 && days.length < 14; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    if (d.getDay() !== 0) days.push(d);
  }
  return days;
}
function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toDateString();
}

// ── SHARED UI ────────────────────────────────────────────────────────────────
function StepIndicator({ current }) {
  const steps = ["Your Details", "Service", "Date & Time", "Confirm"];
  return (
    <div style={s.stepsBar}>
      {steps.map((label, i) => {
        const n = i + 1;
        const isActive = n === current, isDone = n < current;
        return (
          <div key={n} style={{
            display: "flex", alignItems: "center", gap: 8,
            padding: "1rem 1.2rem", fontSize: "0.78rem",
            color: isActive ? C.tealDark : isDone ? C.teal : C.textLight,
            fontWeight: isActive ? 500 : 400,
            borderBottom: isActive ? `2.5px solid ${C.teal}` : "2.5px solid transparent",
          }}>
            <div style={{
              width: 22, height: 22, borderRadius: "50%", flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: "0.68rem", fontWeight: 500,
              background: isActive ? C.teal : isDone ? C.tealLight : C.grayBg,
              border: `1.5px solid ${isActive ? C.teal : isDone ? C.teal : C.grayBorder}`,
              color: isActive ? "#fff" : isDone ? C.tealDark : C.textLight,
            }}>{isDone ? "✓" : n}</div>
            <span style={{ whiteSpace: "nowrap" }}>{label}</span>
          </div>
        );
      })}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <label style={s.label}>{label}</label>
      {children}
    </div>
  );
}

function StyledInput({ type = "text", placeholder, value, onChange }) {
  const [focused, setFocused] = useState(false);
  return (
    <input type={type} placeholder={placeholder} value={value}
      onChange={e => onChange(e.target.value)}
      onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
      style={{ ...s.input, borderColor: focused ? C.teal : C.grayBorder, background: focused ? C.white : C.grayBg }}
    />
  );
}

function StyledSelect({ value, onChange, options }) {
  const [focused, setFocused] = useState(false);
  return (
    <select value={value} onChange={e => onChange(e.target.value)}
      onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
      style={{ ...s.input, cursor: "pointer", borderColor: focused ? C.teal : C.grayBorder }}
    >
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

// ── STEP 1 ────────────────────────────────────────────────────────────────
function Step1({ form, setForm, onNext }) {
  const f = key => ({ value: form[key], onChange: v => setForm(p => ({ ...p, [key]: v })) });
  function validate() {
    if (!form.firstName.trim() || !form.lastName.trim()) return alert("Please enter your full name.");
    if (!form.phone.trim()) return alert("Please enter your phone number.");
    if (!/\S+@\S+\.\S+/.test(form.email)) return alert("Please enter a valid email.");
    onNext();
  }
  return (
    <div style={s.card}>
      <div style={s.cardTitle}>Patient Details</div>
      <div style={s.cardHint}>Please fill in your information accurately.</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginBottom: "1rem" }}>
        <Field label="First Name"><StyledInput placeholder="Arjun" {...f("firstName")} /></Field>
        <Field label="Last Name"><StyledInput placeholder="Sharma" {...f("lastName")} /></Field>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginBottom: "1rem" }}>
        <Field label="Phone Number"><StyledInput type="tel" placeholder="+91 98765 43210" {...f("phone")} /></Field>
        <Field label="Email Address"><StyledInput type="email" placeholder="arjun@email.com" {...f("email")} /></Field>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginBottom: "1rem" }}>
        <Field label="Date of Birth"><StyledInput type="date" {...f("dob")} /></Field>
        <Field label="Gender">
          <StyledSelect value={form.gender} onChange={v => setForm(p => ({ ...p, gender: v }))}
            options={[
              { value: "", label: "Select…" },
              { value: "Male", label: "Male" },
              { value: "Female", label: "Female" },
              { value: "Other", label: "Other" },
              { value: "Prefer not to say", label: "Prefer not to say" },
            ]}
          />
        </Field>
      </div>
      <div style={{ marginBottom: "1rem" }}>
        <Field label="Is this your first visit?">
          <StyledSelect value={form.firstVisit} onChange={v => setForm(p => ({ ...p, firstVisit: v }))}
            options={[
              { value: "", label: "Select…" },
              { value: "yes", label: "Yes – New Patient" },
              { value: "no", label: "No – Returning Patient" },
            ]}
          />
        </Field>
      </div>
      <div style={{ marginBottom: "0.5rem" }}>
        <Field label="Brief description of concern (optional)">
          <textarea value={form.concern} onChange={e => setForm(p => ({ ...p, concern: e.target.value }))}
            placeholder="e.g. Lower back pain for 2 weeks…"
            style={{ ...s.input, minHeight: 70, resize: "vertical" }}
          />
        </Field>
      </div>
      <button style={s.btnPrimary} onClick={validate}>Continue →</button>
    </div>
  );
}

// ── STEP 2 ────────────────────────────────────────────────────────────────
function Step2({ selected, onSelect, onNext }) {
  return (
    <div style={s.card}>
      <div style={s.cardTitle}>Select Service</div>
      <div style={s.cardHint}>Choose the type of physiotherapy session you need.</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(155px, 1fr))", gap: "0.6rem", marginBottom: "1rem" }}>
        {SERVICES.map(svc => {
          const active = selected === svc.id;
          return (
            <div key={svc.id} onClick={() => onSelect(svc.id)} style={{
              border: `1.5px solid ${active ? C.teal : C.grayBorder}`,
              borderRadius: 10, padding: "0.75rem 0.9rem",
              cursor: "pointer", background: active ? C.tealLight : C.grayBg,
              transition: "all 0.15s",
            }}>
              <div style={{ fontSize: "0.85rem", fontWeight: 500, color: active ? C.tealDark : C.textDark }}>{svc.name}</div>
              <div style={{ fontSize: "0.73rem", color: active ? C.teal : C.textLight, marginTop: 2 }}>{svc.dur}</div>
            </div>
          );
        })}
      </div>
      <button style={s.btnPrimary} onClick={() => { if (!selected) { alert("Please select a service."); return; } onNext(); }}>Continue →</button>
    </div>
  );
}

// ── STEP 3 ────────────────────────────────────────────────────────────────
function Step3({ booking, setBooking, existingBookings, loadingSlots, onNext }) {
  const dates = getNext14Days();
  const svc = SERVICES.find(s => s.id === booking.service);

  // For each slot, check if ANY cabin is available
  function isSlotAvailable(slotTime, slotIdx) {
    for (const cabin of CABINS) {
      if (slotIdx > cabin.maxSlotIdx) continue;
      const taken = existingBookings.some(
        b => b.date === booking.date && b.time === slotTime && b.cabin === cabin.name
      );
      if (!taken) return true;
    }
    return false;
  }

  function handleSlotClick(slotTime) {
    setBooking(p => ({ ...p, slot: slotTime }));
  }

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 270px", gap: "1.25rem", alignItems: "start" }}>
      <div>
        {/* Date */}
        <div style={s.card}>
          <div style={s.cardTitle}>Select Date</div>
          <div style={s.cardHint}>Pick an available date (Sundays excluded).</div>
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
            {dates.map(d => {
              const iso = d.toISOString().split("T")[0];
              const active = booking.date === iso;
              return (
                <div key={iso} onClick={() => setBooking(p => ({ ...p, date: iso, slot: null }))}
                  style={{
                    border: `1.5px solid ${active ? C.teal : C.grayBorder}`,
                    borderRadius: 10, padding: "0.5rem 0.85rem",
                    cursor: "pointer", background: active ? C.tealLight : C.grayBg,
                    textAlign: "center", minWidth: 60, transition: "all 0.15s",
                  }}>
                  <div style={{ fontSize: "0.68rem", color: active ? C.teal : C.textLight }}>{DAYS[d.getDay()]}</div>
                  <div style={{ fontSize: "1rem", fontWeight: 500, color: active ? C.tealDark : C.textDark }}>{d.getDate()}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Time slots */}
        <div style={s.card}>
          <div style={s.cardTitle}>Select Time</div>
          <div style={s.cardHint}>
            {booking.date
              ? "A cabin will be automatically assigned when you book."
              : "Please select a date first."}
          </div>

          {/* Legend */}
          <div style={{ display: "flex", gap: "1.25rem", flexWrap: "wrap", marginBottom: "1rem", fontSize: "0.75rem", color: C.textMid }}>
            {[
              { bg: C.tealLight, border: C.teal, label: "Available" },
              { bg: C.redLight,  border: C.red,  label: "Fully booked" },
              { bg: "#f0ede6",   border: C.grayBorder, label: "Break / gap" },
            ].map(l => (
              <div key={l.label} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <div style={{ width: 12, height: 12, borderRadius: 3, background: l.bg, border: `1.5px solid ${l.border}` }} />
                {l.label}
              </div>
            ))}
          </div>

          {loadingSlots ? (
            <div style={{ textAlign: "center", padding: "2rem 0", color: C.textLight, fontSize: "0.85rem" }}>
              Checking availability…
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(125px, 1fr))", gap: "0.55rem" }}>
              {SCHEDULE.map((slot, idx) => {
                const isBreak = slot.type === "gap" || slot.type === "lunch";
                const available = !isBreak && booking.date && isSlotAvailable(slot.time, idx);
                const unavailableBookable = !isBreak && booking.date && !available;
                const notSelected = !booking.date;
                const selected = booking.slot === slot.time;

                let bg, border, timeColor, lblColor, label, clickable;

                if (isBreak) {
                  bg = "#f0ede6"; border = C.grayBorder;
                  timeColor = C.textLight; lblColor = C.textLight;
                  label = slot.type === "lunch" ? "Lunch break" : "Gap";
                  clickable = false;
                } else if (notSelected) {
                  bg = C.grayBg; border = C.grayBorder;
                  timeColor = C.textLight; lblColor = C.textLight;
                  label = "Select date"; clickable = false;
                } else if (unavailableBookable) {
                  bg = C.redLight; border = C.red;
                  timeColor = C.red; lblColor = C.red;
                  label = "Fully booked"; clickable = false;
                } else {
                  // available
                  bg = selected ? C.tealLight : C.grayBg;
                  border = selected ? C.teal : C.grayBorder;
                  timeColor = selected ? C.tealDark : C.textDark;
                  lblColor = selected ? C.teal : C.textLight;
                  label = "Available"; clickable = true;
                }

                return (
                  <div key={slot.time} onClick={() => clickable && handleSlotClick(slot.time)}
                    style={{
                      border: `1.5px solid ${border}`, borderRadius: 10,
                      padding: "0.65rem 0.5rem", textAlign: "center",
                      cursor: clickable ? "pointer" : "not-allowed",
                      background: bg, opacity: isBreak ? 0.45 : 1,
                      transition: "all 0.15s",
                    }}>
                    <div style={{ fontSize: "0.78rem", fontWeight: 500, color: timeColor }}>{slot.time}</div>
                    <div style={{ fontSize: "0.67rem", color: lblColor, marginTop: 2 }}>{label}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <button style={s.btnPrimary}
          onClick={() => { if (!booking.date || !booking.slot) { alert("Please select a date and time."); return; } onNext(); }}>
          Review & Confirm →
        </button>
      </div>

      {/* Summary sidebar */}
      <div style={s.summaryCard}>
        <div style={{ fontFamily: "'Playfair Display', serif", fontSize: "1rem", marginBottom: "1rem", color: "#fff" }}>
          Booking Summary
        </div>
        {(!booking.date && !booking.slot) ? (
          <div style={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.4)", textAlign: "center", padding: "1rem 0" }}>
            Select a date and time to see your summary
          </div>
        ) : (
          <>
            {[
              ["Service", svc?.name || "—"],
              ["Duration", svc?.dur || "—"],
              ["Date", fmtDate(booking.date)],
              ["Time", booking.slot || "—"],
            ].map(([k, v]) => (
              <div key={k} style={{ display: "flex", justifyContent: "space-between", fontSize: "0.82rem", marginBottom: "0.55rem" }}>
                <span style={{ color: "rgba(255,255,255,0.65)" }}>{k}</span>
                <span style={{ color: "#fff", fontWeight: 500, textAlign: "right", maxWidth: 140 }}>{v}</span>
              </div>
            ))}
            <div style={{ height: 1, background: "rgba(255,255,255,0.15)", margin: "0.75rem 0" }} />
            <div style={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.5)", lineHeight: 1.5 }}>
              A cabin will be automatically assigned to you upon confirmation.
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── STEP 4 ────────────────────────────────────────────────────────────────
function Step4({ form, booking, assignedCabin, onSubmit, loading, success, refNum }) {
  const svc = SERVICES.find(s => s.id === booking.service);

  if (success) return (
    <div style={{ ...s.card, textAlign: "center", padding: "3rem 1.5rem" }}>
      <div style={{
        width: 72, height: 72, borderRadius: "50%", background: C.tealLight,
        display: "flex", alignItems: "center", justifyContent: "center",
        margin: "0 auto 1.5rem", fontSize: "1.8rem", color: C.teal,
      }}>✓</div>
      <div style={{ fontFamily: "'Playfair Display', serif", fontSize: "1.6rem", color: C.tealDark, marginBottom: "0.5rem" }}>
        Booking Confirmed!
      </div>
      <p style={{ fontSize: "0.88rem", color: C.textMid, maxWidth: 360, margin: "0 auto 0.75rem", lineHeight: 1.7 }}>
        Thank you! Your appointment has been booked. A confirmation SMS and email will be sent to you shortly.
      </p>
      {assignedCabin && (
        <p style={{ fontSize: "0.82rem", color: C.tealDark, marginBottom: "1.5rem" }}>
          You've been assigned to <strong>{assignedCabin}</strong>.
        </p>
      )}
      <div style={{
        display: "inline-block", background: C.tealLight,
        border: `1px solid ${C.tealMid}`, borderRadius: 8,
        padding: "0.5rem 1.5rem", fontSize: "0.9rem", fontWeight: 500,
        color: C.tealDark, letterSpacing: 1,
      }}>REF: {refNum}</div>
    </div>
  );

  return (
    <div style={s.card}>
      <div style={s.cardTitle}>Review Your Booking</div>
      <div style={s.cardHint}>Verify all details before confirming.</div>
      <table style={{ width: "100%", fontSize: "0.88rem", borderCollapse: "collapse" }}>
        <tbody>
          {[
            ["Patient", `${form.firstName} ${form.lastName}`],
            ["Phone", form.phone],
            ["Email", form.email],
            ["Service", `${svc?.name} (${svc?.dur})`],
            ["Date", fmtDate(booking.date)],
            ["Time", booking.slot],
            ["Cabin", "Auto-assigned on confirmation"],
          ].map(([k, v]) => (
            <tr key={k}>
              <td style={{ padding: "0.5rem 0", color: C.textMid, width: 140 }}>{k}</td>
              <td style={{ fontWeight: k === "Cabin" ? 400 : 500, color: k === "Cabin" ? C.textLight : C.textDark, fontStyle: k === "Cabin" ? "italic" : "normal" }}>{v}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{
        background: C.amberLight, border: `1px solid #f5c97a`,
        borderRadius: 8, padding: "0.6rem 0.9rem",
        fontSize: "0.78rem", color: "#7a5300", marginTop: "1rem",
      }}>
        ⏰ Please arrive 5 minutes early. Cancellations must be made at least 4 hours in advance.
      </div>
      <button style={{ ...s.btnPrimary, opacity: loading ? 0.7 : 1, cursor: loading ? "not-allowed" : "pointer" }}
        onClick={onSubmit} disabled={loading}>
        {loading ? "Confirming…" : "Confirm Booking ✓"}
      </button>
    </div>
  );
}

// ── MAIN ──────────────────────────────────────────────────────────────────
export default function BookingPage() {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [success, setSuccess] = useState(false);
  const [refNum, setRefNum] = useState("");
  const [assignedCabin, setAssignedCabin] = useState("");
  const [existingBookings, setExistingBookings] = useState([]); // fetched from API

  const [form, setForm] = useState({
    firstName: "", lastName: "", phone: "", email: "",
    dob: "", gender: "", firstVisit: "", concern: "",
  });
  const [booking, setBooking] = useState({
    service: null, date: null, slot: null,
  });

  // Load Google Fonts
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600&family=DM+Sans:wght@300;400;500&display=swap";
    document.head.appendChild(link);
  }, []);

  // Fetch existing bookings for the selected date so we can show real availability
  useEffect(() => {
    if (!booking.date) return;
    setLoadingSlots(true);
    fetch(`https://brightlifephysio.com/api/check-availability.php?date=${booking.date}`)
      .then(r => r.json())
      .then(data => setExistingBookings(data.bookings || []))
      .catch(() => setExistingBookings([]))   // fallback: assume all free
      .finally(() => setLoadingSlots(false));
  }, [booking.date]);

  async function submitBooking() {
    // Auto-assign cabin on the client side first (server should also validate & assign)
    const cabin = autoAssignCabin(booking.date, booking.slot, existingBookings);
    if (!cabin) return alert("Sorry, all cabins are fully booked for this slot. Please choose another time.");

    setLoading(true);
    const payload = { ...form, ...booking, cabin };

    try {
      // ── REPLACE WITH YOUR API ENDPOINT ──────────────────────────────────
      const res = await fetch("https://brightlifephysio.com/api/book.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        setRefNum(data.booking_ref || ("BLP-" + Math.random().toString(36).substr(2, 6).toUpperCase()));
        setAssignedCabin(data.assigned_cabin || cabin);
        setSuccess(true);
      } else {
        alert("Booking failed. Please try again or call us directly.");
      }
    } catch {
      // Demo fallback
      setRefNum("BLP-" + Math.random().toString(36).substr(2, 6).toUpperCase());
      setAssignedCabin(cabin);
      setSuccess(true);
    } finally {
      setLoading(false);
    }
  }

  function goStep(n) {
    setStep(n);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  return (
    <div style={s.page}>
      <div style={s.hero}>
        <div style={s.heroLogo}>Bright<span style={{ color: C.tealMid }}>Life</span> Physio</div>
        <div style={s.heroSub}>Physiotherapy & Wellness</div>
        <div style={s.heroTitle}>Book Your Session</div>
        <p style={s.heroDesc}>Choose your preferred time and service. We'll handle the rest.</p>
      </div>

      <StepIndicator current={step} />

      <div style={s.main}>
        {step === 1 && <Step1 form={form} setForm={setForm} onNext={() => goStep(2)} />}
        {step === 2 && (
          <Step2
            selected={booking.service}
            onSelect={id => setBooking(p => ({ ...p, service: id }))}
            onNext={() => goStep(3)}
          />
        )}
        {step === 3 && (
          <Step3
            booking={booking} setBooking={setBooking}
            existingBookings={existingBookings}
            loadingSlots={loadingSlots}
            onNext={() => goStep(4)}
          />
        )}
        {step === 4 && (
          <Step4
            form={form} booking={booking}
            assignedCabin={assignedCabin}
            onSubmit={submitBooking}
            loading={loading} success={success} refNum={refNum}
          />
        )}
      </div>
    </div>
  );
}